<template>
  <div class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center">
    <div v-if="currentDevice && layout" class="relative flex-1 flex items-center justify-center">
      <MouseLayoutPanel
        :key-buttons="mouseKeys"
        :key-urls="activeKeyUrl"
        :img="imgUrl"
        :current="activeKey"
        @key-click="onKeyClick"
        @update-setting="handleMouseKeyDrop"
      >
        <template #key-label="{ mouseKey }">
          <Tooltip
            v-if="isKeyModified(mouseKey)"
            side="bottom"
            :content="$t('keys.targetPanel.restoreKey')"
          >
            <span>
              <Icon
                icon="Function-Wireframe-CCW"
                class="w-4 h-4"
                @click.stop="() => handleRestoreKey(mouseKey)"
              />
            </span>
          </Tooltip>
          {{ getKeyLabel(mouseKey) }}
        </template>
      </MouseLayoutPanel>
    </div>
    <Tabs
      v-model="activeTabValue"
      class="relative -mt-14.5 shrink-0 mb-2 w-full h-82 flex flex-col items-center gap-4.5"
    >
      <TabsList class="p-0 flex items-center gap-4 h-10">
        <TabsTrigger v-for="tab in tabConfigs" :key="tab.value" :value="tab.value">
          <Button color="glass" :active="tab.value === activeTabValue" class="h-10">
            {{ $t(tab.label) }}
          </Button>
        </TabsTrigger>
      </TabsList>

      <transition
        name="fade"
        mode="out-in"
        enter-active-class="animate-in fade-in duration-150"
        leave-active-class="animate-out fade-out duration-150"
      >
        <component :is="activeTabComponent" />
      </transition>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
  import {
    defineAsyncComponent,
    markRaw,
    computed,
    ref,
    provide,
    shallowRef,
    onMounted,
    watch,
  } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { LayoutKey } from '@rapoo/device-sdk'
  import { Button, Tabs, TabsList, TabsTrigger, Icon, Tooltip, toast } from '@rapoo/element'
  import { useConfigurationStore, useDeviceStore, useLocaleStore } from '@/stores'
  import { getImageUrl } from '@/utils/assets'
  import MouseLayoutPanel from './components/MouseLayoutPanel.vue'
  import { KeyChangePayload, KeySetting, SelectedKey } from './types'
  import {
    KeySettingsSymbol,
    SelectedKeySymbol,
    UpdateSelectedKeyMappingSymbol,
    RestoreKeyMappingWithTargetSymbol,
  } from './types'
  import { useKeySDK } from './composables/useKeySDK'
  import { useMouseMacroRepository } from './composables/useMouseMacroRepository'
  import { createCoalesceLatestRequestQueue } from '@/utils/coalesceLatestRequest'
  import {
    buildKeySettingFromPayload,
    buildRestoreKeyMapPayload,
    buildSdkKeyMapPayloadFromSetting,
    parseMapToKeySettings,
    parseNumber,
  } from './utils/keyMapSetting'
  import { useKeyLabel } from './composables/useKeyLabel'

  const tabComponentMap = {
    normal: markRaw(defineAsyncComponent(() => import('./components/TabNormal.vue'))),
    media: markRaw(defineAsyncComponent(() => import('./components/TabMedia.vue'))),
    keyboard: markRaw(defineAsyncComponent(() => import('./components/TabKeyboard.vue'))),
    combinedKey: markRaw(defineAsyncComponent(() => import('./components/TabCombinedKey.vue'))),
    macro: markRaw(defineAsyncComponent(() => import('./components/TabMacro.vue'))),
  } as const

  const tabConfigs = computed(() => [
    {
      label: 'keys.tabs.normal',
      value: 'normal',
      component: tabComponentMap.normal,
    },
    {
      label: 'keys.tabs.media',
      value: 'media',
      component: tabComponentMap.media,
    },
    {
      label: 'keys.tabs.keyboardKeys',
      value: 'keyboard',
      component: tabComponentMap.keyboard,
    },
    {
      label: 'keys.tabs.combinedKeys',
      value: 'combinedKey',
      component: tabComponentMap.combinedKey,
    },
    {
      label: 'keys.tabs.macro',
      value: 'macro',
      component: tabComponentMap.macro,
    },
  ])

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const layout = computed(() => currentDevice.value?.manifest.layout)

  const { initKeys, setKey } = useKeySDK()
  const { macroList: repoMacroList, loadMacroList } = useMouseMacroRepository()

  const activeTabValue = ref<(typeof tabConfigs.value)[number]['value']>(tabConfigs.value[0].value)
  const activeTabComponent = computed(() => {
    return tabConfigs.value.find((tab) => tab.value === activeTabValue.value)?.component
  })

  const mouseKeys = computed(() => layout.value?.keys[0] || [])
  const currentDeviceColor = computed(() => {
    if (!currentDevice.value?.manifest?.ui.colors) return

    const { series, current } = currentDevice.value?.manifest?.ui.colors
    if (!series.length) return

    return series.find((color) => color.key === current)
  })

  const imgUrl = computed(() => {
    const ui = currentDevice.value?.manifest?.ui
    const color = currentDeviceColor.value
    if (!ui?.assetsDir || !color?.key) return ''
    return getImageUrl(`${ui.assetsDir}/func_${color.key}.png`)
  })

  const activeKey = ref<LayoutKey>()
  const activeKeyUrl = ref<string[]>([])

  // 定义键盘数据
  const keySettings = ref<KeySetting[]>([])
  provide(KeySettingsSymbol, keySettings)

  // 当前选中的按键
  const selectedKey = shallowRef<SelectedKey>(null)
  provide(SelectedKeySymbol, selectedKey)

  /**
   * 用真实宏名称替换 keySettings 中的占位名称（"Macro {index}"），
   * 使虚拟鼠标按键 tooltip 显示用户真实命名的宏。
   */
  const resolveMacroNames = (settings: KeySetting[], macros: { index: number; name: string }[]) => {
    const nameMap = new Map(macros.map((m) => [m.index, m.name]))
    for (const setting of settings) {
      if (setting.keyType !== 'macro' || !setting.raw?.macro) continue
      const realName = nameMap.get(setting.raw.macro.index)
      if (realName) {
        setting.raw.macroName = realName
      }
    }
  }

  const getKeySettings = async () => {
    const keyMap = await initKeys()

    keySettings.value = parseMapToKeySettings({ keyMap, layoutKeys: layout.value?.keys })
    // 回显宏按键时，用 macroRepository 已加载的列表解析宏名称
    const hasMacroKeys = keySettings.value.some((s) => s.keyType === 'macro')
    if (hasMacroKeys) {
      if (!repoMacroList.value.length) {
        await loadMacroList()
      }
      if (repoMacroList.value.length) {
        resolveMacroNames(keySettings.value, repoMacroList.value)
        keySettings.value = [...keySettings.value]
      }
    }
  }

  /** 板载切换上报后的按键回读：最多 1 in-flight + 1 pending */
  const cfgKeyFetchQueue = createCoalesceLatestRequestQueue('mouseKeysPage.cfgReport')
  const scheduleGetKeySettings = () => {
    cfgKeyFetchQueue.enqueue(() => getKeySettings())
  }

  const getKeyImgUrl = (key: LayoutKey) => {
    const ui = currentDevice.value?.manifest?.ui
    if (!ui?.assetsDir) return ''

    return getImageUrl(`${ui.assetsDir}/${key.url}.svg`)
  }

  onMounted(() => {
    scheduleGetKeySettings()
  })

  watch(
    mouseKeys,
    (keys) => {
      if (activeKey.value || !keys.length) return

      const firstKey = keys[0]
      activeKey.value = firstKey
      selectedKey.value = firstKey
      activeKeyUrl.value = [getKeyImgUrl(firstKey)]
    },
    { immediate: true },
  )

  const { t: $toastT } = useLocaleStore()
  const configuration = useConfigurationStore()
  const { currentConfig } = storeToRefs(configuration)

  watch(
    () => currentConfig.value?.index,
    () => {
      scheduleGetKeySettings()
    },
  )

  const findSettingIndexByTargetKey = (settings: KeySetting[], targetKey: SelectedKey) => {
    if (!targetKey?.keywords) return -1
    return settings.findIndex((setting) => setting.keywords === targetKey.keywords)
  }

  const resolveTargetIndex = (targetKey: SelectedKey, existingSetting?: KeySetting) => {
    const settingIndex = parseNumber(existingSetting?.raw?.index)
    if (settingIndex != null) return settingIndex

    return parseNumber(targetKey?.key)
  }

  const resolveMappingTargetKeywords = (target: KeySetting['target']): string => {
    return target && typeof target === 'object' && 'keywords' in target
      ? String(target.keywords)
      : ''
  }

  // ============ 改键校验逻辑 =================
  // 滚轮相关 keywords
  const WHEEL_KEYWORDS = new Set(['上滚', '下滚', '左滚', '右滚'])

  // 滚轮按键不支持的功能 keywords
  const WHEEL_RESTRICTED_TARGETS = new Set([
    'Fire button',
    'Sniper button',
    'DPI+',
    'DPI-',
    'DPI cycle+',
    'DPI cycle-',
    'Config cycle',
    'Dongle cycle',
  ])

  /**
   * 检查排除指定按键后是否仍至少存在一个左击功能
   * @param excludeKeywords 要排除的按键 keywords（模拟该按键不再提供左击功能）
   * @returns true 表示仍存在左击，false 表示没有左击了
   */
  const hasLeftClickAvailable = (excludeKeywords: string): boolean => {
    const otherSettings = keySettings.value.filter((s) => s.keywords !== excludeKeywords)

    // 是否有其他改键被映射为左击
    const hasOtherMappedLeftClick = otherSettings.some(
      (s) => (s.target as any)?.keywords === '左击',
    )

    // 物理左键是否仍保持左击功能
    // 检查条件：1) keySettings 中没有把左击改掉的记录; 2) 当前排除的不是左击本身
    // 因为如果当前操作就是要改左击（excludeKeywords === '左击'），则物理左键将不再提供左击
    const physicalLeftClickKept =
      !keySettings.value.some(
        (s) => s.keywords === '左击' && (s.target as any)?.keywords !== '左击',
      ) && excludeKeywords !== '左击'

    return hasOtherMappedLeftClick || physicalLeftClickKept
  }

  /**
   * 校验改键操作是否合法
   * @returns true 表示校验通过，false 表示被拦截
   */
  const validateKeyMapping = (sourceData: KeyChangePayload, targetKey: SelectedKey): boolean => {
    if (!targetKey || !sourceData) return false

    const targetKeywords = resolveMappingTargetKeywords(sourceData.target)

    // 更改左击相关的按键为非左击时，需要至少保留一个左键
    if (targetKeywords !== '左击') {
      // 规则1：物理左键被改成非左击
      // 规则2：当前映射为左击的非左键被改成非左击
      const currentlyMappedToLeftClick =
        (keySettings.value.find((s) => s.keywords === targetKey.keywords)?.target as any)
          ?.keywords === '左击'

      if (targetKey.keywords === '左击' || currentlyMappedToLeftClick) {
        if (!hasLeftClickAvailable(targetKey.keywords)) {
          toast.info($toastT('toast.keys.atLeastOneLeftClick'))
          return false
        }
      }
    }

    // 规则3：不可以将其他键改成滚轮键
    if (WHEEL_KEYWORDS.has(targetKeywords) && !WHEEL_KEYWORDS.has(targetKey.keywords)) {
      toast.info($toastT('toast.keys.wheelOnly'))
      return false
    }

    // 规则4：不可以将滚轮键改成火力键、狙击键、DPI相关、配置切换、接收器切换
    if (WHEEL_KEYWORDS.has(targetKey.keywords) && WHEEL_RESTRICTED_TARGETS.has(targetKeywords)) {
      toast.info($toastT('toast.keys.wheelRestricted'))
      return false
    }

    return true
  }

  const updateKeyMappingWithData = (sourceData: KeyChangePayload, targetKey: SelectedKey) => {
    if (!targetKey || !sourceData) return

    if (!validateKeyMapping(sourceData, targetKey)) return

    const currentSettings = [...keySettings.value]
    const existingIndex = findSettingIndexByTargetKey(currentSettings, targetKey)
    const existingSetting = existingIndex >= 0 ? currentSettings[existingIndex] : undefined
    const targetIndex = resolveTargetIndex(targetKey, existingSetting)
    if (targetIndex == null) return

    const nextTargetKeywords = resolveMappingTargetKeywords(sourceData.target)
    const currentTargetKeywords = existingSetting
      ? resolveMappingTargetKeywords(existingSetting.target)
      : targetKey.keywords
    const needsDeviceWrite =
      !nextTargetKeywords || !currentTargetKeywords || nextTargetKeywords !== currentTargetKeywords

    const newSetting = buildKeySettingFromPayload({
      sourceData: {
        ...sourceData,
        keywords: targetKey.keywords,
      },
      index: targetIndex,
    })
    if (!newSetting) return

    if (existingIndex >= 0) {
      currentSettings[existingIndex] = newSetting
    } else {
      currentSettings.push(newSetting)
    }

    keySettings.value = currentSettings

    // 相同映射仍更新页面回显，但不重复向设备写入；若当前已被改成其它功能，则正常下发。
    if (!needsDeviceWrite) return

    const payload = buildSdkKeyMapPayloadFromSetting(newSetting)
    if (!payload) return

    setKey(payload)
  }

  const updateSelectedKeyMapping = (data: KeyChangePayload) => {
    if (!selectedKey.value || !data) return

    updateKeyMappingWithData(data, selectedKey.value)
  }

  const handleMouseKeyDrop = (sourceData: KeyChangePayload, targetKey: SelectedKey) => {
    if (activeTabValue.value === 'combinedKey') return

    updateKeyMappingWithData(sourceData, targetKey)
  }

  provide(UpdateSelectedKeyMappingSymbol, updateSelectedKeyMapping)

  const onKeyClick = (data: LayoutKey) => {
    activeKey.value = data
    selectedKey.value = data
    activeKeyUrl.value = [getKeyImgUrl(data)]
  }

  const restoreKeyMappingWithTarget = (targetKey: SelectedKey) => {
    if (!targetKey) return

    const settingIndex = keySettings.value.findIndex(
      (s) => s.keywords === targetKey.keywords && s.isModified,
    )
    if (settingIndex < 0) return

    const setting = keySettings.value[settingIndex]
    if (!setting) return

    // 恢复校验：如果要恢复的按键被映射为左键功能，恢复后至少保留一个左键
    if (targetKey.keywords !== '左击' && (setting.target as any)?.keywords === '左击') {
      if (!hasLeftClickAvailable(targetKey.keywords)) {
        toast.info($toastT('toast.keys.atLeastOneLeftClick'))
        return
      }
    }

    const payload = buildRestoreKeyMapPayload(setting)
    if (!payload) return

    // 从 keySettings 中移除该项
    const currentSettings = [...keySettings.value]
    currentSettings.splice(settingIndex, 1)
    keySettings.value = currentSettings

    // 发送恢复默认的指令到 SDK
    setKey(payload)
  }

  provide(RestoreKeyMappingWithTargetSymbol, restoreKeyMappingWithTarget)

  const handleRestoreKey = (mouseKey: LayoutKey) => {
    restoreKeyMappingWithTarget(mouseKey)
  }

  // ============ 按键标签显示逻辑 =================
  const { isKeyModified, getKeyLabel } = useKeyLabel(keySettings)
</script>
